import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Drawer,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useFetch } from '../../../hooks/useFetch';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import useHandleErrors from '../../../hooks/useHandleErrors';

const STATUS_OPTIONS = [
    { value: 'CONCLUDED', label: 'Pago', bgcolor: '#e8f5e9', color: '#2e7d32' },
    { value: 'DRAFT', label: 'Sem pagamento', bgcolor: '#fff3e0', color: '#e65100' },
    { value: 'CANCELED', label: 'Cancelado', bgcolor: '#fce4ec', color: '#c62828' },
    { value: 'REJECTED', label: 'Rejeitado', bgcolor: '#fce4ec', color: '#c62828' },
    { value: 'CLOSED', label: 'Fechada', bgcolor: '#e3f2fd', color: '#1565c0' },
];

const TYPE_OPTIONS = [
    { value: 'schedule', label: 'Agendamento' },
    { value: 'raffle', label: 'Sorteio' },
];

const statusMap = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

export default () => {
    const [filterView, setFilterView] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Filtros
    const [filterStatuses, setFilterStatuses] = useState([]);
    const [filterTypes, setFilterTypes] = useState([]);
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterPriceRange, setFilterPriceRange] = useState([0, 500]);

    const [appliedFilters, setAppliedFilters] = useState({});

    const { data, error, isLoading } = useFetch('/backoffice/orders', { params: appliedFilters });
    useHandleErrors(error);

    const buildParams = () => {
        const params = {};
        if (searchText.trim()) params.search = searchText.trim();
        if (filterStatuses.length > 0) params.status = filterStatuses.join(',');
        if (filterTypes.length === 1) params.type = filterTypes[0];
        if (filterDateFrom) params.datetime = [filterDateFrom, filterDateTo || filterDateFrom];
        if (filterPriceRange[0] > 0) params.price_min = filterPriceRange[0];
        if (filterPriceRange[1] < 500) params.price_max = filterPriceRange[1];
        return params;
    };

    const handleSearch = () => setAppliedFilters(buildParams());
    const handleSearchKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

    const applyFilters = () => {
        setAppliedFilters(buildParams());
        setFilterView(false);
    };

    const clearFilters = () => {
        setFilterStatuses([]);
        setFilterTypes([]);
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterPriceRange([0, 500]);
        setAppliedFilters(searchText.trim() ? { search: searchText.trim() } : {});
        setFilterView(false);
    };

    const activeFilterCount = [
        filterStatuses.length > 0,
        filterTypes.length > 0,
        filterDateFrom,
        filterPriceRange[0] > 0 || filterPriceRange[1] < 500,
    ].filter(Boolean).length;

    const toggleCheckbox = (list, setList, value) => {
        setList(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const columns = useMemo(() => [
        { field: 'id', headerName: 'ID', width: 70 },
        {
            field: 'meeting',
            headerName: 'Anfitriao',
            flex: 1,
            minWidth: 150,
            valueGetter: ({ value }) => value?.Profile?.User?.name || 'Anonimo',
        },
        {
            field: 'user',
            headerName: 'Conviva',
            flex: 1,
            minWidth: 150,
            valueGetter: ({ value }) => value?.name || 'Não designado',
        },
        {
            field: 'type',
            headerName: 'Tipo',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => (row.meeting?.raffle ? 'Sorteio' : 'Agendamento'),
            renderCell: ({ value }) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        bgcolor: value === 'Sorteio' ? '#f3e8ff' : '#e6f3f1',
                        color: value === 'Sorteio' ? '#7c3aed' : '#006a61',
                        fontWeight: 500,
                    }}
                />
            ),
        },
        {
            field: 'datetime',
            headerName: 'Data e hora',
            width: 160,
            valueGetter: ({ value }) => new Date(value),
            renderCell: ({ value }) => (
                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                    {value.toLocaleString('pt-BR')}
                </Typography>
            ),
        },
        {
            field: 'amount',
            headerName: 'Valor',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ value }) => (
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                    {value ? `R$ ${Number(value).toFixed(2).replace('.', ',')}` : 'Gratis'}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ value }) => {
                const props = statusMap[value] || { label: value, bgcolor: '#f5f5f5', color: '#666' };
                return (
                    <Chip
                        label={props.label}
                        size="small"
                        sx={{ bgcolor: props.bgcolor, color: props.color, fontWeight: 500 }}
                    />
                );
            },
        },
        {
            field: 'payment_id',
            headerName: 'ID Pagar.me',
            width: 160,
            renderCell: ({ value }) => (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {value || '-'}
                </Typography>
            ),
        },
    ]);

    return (
        <>
            <Box sx={{
                maxWidth: 1100,
                mx: 'auto',
                p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} color="#333">
                        Vendas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Acompanhe as vendas da plataforma
                    </Typography>
                </Box>

                {/* Barra de pesquisa + filtros */}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Pesquisar por conviva ou anfitrião..."
                        value={searchText}
                        onChange={e => {
                            const val = e.target.value;
                            setSearchText(val);
                            if (!val.trim()) setAppliedFilters(prev => { const p = { ...prev }; delete p.search; return p; });
                        }}
                        onKeyDown={handleSearchKeyDown}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#999', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchText && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchText(''); setAppliedFilters(prev => { const p = { ...prev }; delete p.search; return p; }); }}>
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                bgcolor: 'white',
                            },
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleSearch}
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            borderColor: '#006a61',
                            color: '#006a61',
                            fontWeight: 500,
                            borderRadius: '10px',
                            height: 40,
                            px: 2.5,
                            '&:hover': { borderColor: '#005a52', bgcolor: '#f0f7f6' },
                        }}
                    >
                        Buscar
                    </Button>
                    <Button
                        variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                        onClick={() => setFilterView(true)}
                        startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '10px',
                            height: 40,
                            px: 2,
                            whiteSpace: 'nowrap',
                            ...(activeFilterCount > 0
                                ? { bgcolor: '#006a61', '&:hover': { bgcolor: '#005a52' } }
                                : { borderColor: '#ccc', color: '#666', '&:hover': { borderColor: '#999', bgcolor: '#fafafa' } }
                            ),
                        }}
                    >
                        Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    </Button>
                </Stack>

                {/* Tabela */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: '16px',
                    border: '1px solid #e8e8e8',
                    overflow: 'hidden',
                    height: 620,
                }}>
                    <DataGrid
                        loading={isLoading}
                        rows={data || []}
                        columns={columns}
                        disableRowSelectionOnClick
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#fafafa',
                                borderBottom: '1px solid #e8e8e8',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#555',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f5f5f5',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: '#f9fffe',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                display: 'none',
                            },
                        }}
                    />
                </Box>
            </Box>

            {/* Drawer de filtros */}
            <Drawer
                anchor="right"
                variant="temporary"
                open={filterView}
                onClose={() => setFilterView(false)}
                PaperProps={{ sx: { borderRadius: '16px 0 0 16px' } }}
            >
                <Box sx={{ width: 320, p: 3, overflowY: 'auto' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="#333">
                            Filtros
                        </Typography>
                        <IconButton onClick={() => setFilterView(false)} size="small">
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>

                    <Stack spacing={3}>
                        {/* Status de pagamento */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1 }}>
                                Status de pagamento
                            </Typography>
                            <FormGroup>
                                {STATUS_OPTIONS.map(opt => (
                                    <FormControlLabel
                                        key={opt.value}
                                        control={
                                            <Checkbox
                                                checked={filterStatuses.includes(opt.value)}
                                                onChange={() => toggleCheckbox(filterStatuses, setFilterStatuses, opt.value)}
                                                size="small"
                                                sx={{ '&.Mui-checked': { color: '#006a61' } }}
                                            />
                                        }
                                        label={
                                            <Chip
                                                label={opt.label}
                                                size="small"
                                                sx={{ bgcolor: opt.bgcolor, color: opt.color, fontWeight: 500, cursor: 'pointer' }}
                                            />
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </Box>

                        {/* Tipo */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1 }}>
                                Tipo de encontro
                            </Typography>
                            <FormGroup>
                                {TYPE_OPTIONS.map(opt => (
                                    <FormControlLabel
                                        key={opt.value}
                                        control={
                                            <Checkbox
                                                checked={filterTypes.includes(opt.value)}
                                                onChange={() => toggleCheckbox(filterTypes, setFilterTypes, opt.value)}
                                                size="small"
                                                sx={{ '&.Mui-checked': { color: '#006a61' } }}
                                            />
                                        }
                                        label={<Typography variant="body2">{opt.label}</Typography>}
                                    />
                                ))}
                            </FormGroup>
                        </Box>

                        {/* Data */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1 }}>
                                Período
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="De"
                                    placeholder="dd/mm/aaaa"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                                    value={filterDateFrom ? filterDateFrom.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') : ''}
                                    onChange={e => {
                                        let v = e.target.value.replace(/\D/g, '');
                                        if (v.length > 8) v = v.slice(0, 8);
                                        if (v.length >= 5) v = v.slice(0, 2) + '/' + v.slice(2, 4) + '/' + v.slice(4);
                                        else if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                        const parts = v.split('/');
                                        if (parts.length === 3 && parts[2]?.length === 4) {
                                            setFilterDateFrom(`${parts[2]}-${parts[1]}-${parts[0]}`);
                                        } else {
                                            setFilterDateFrom(v);
                                        }
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Até"
                                    placeholder="dd/mm/aaaa"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                                    value={filterDateTo ? filterDateTo.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') : ''}
                                    onChange={e => {
                                        let v = e.target.value.replace(/\D/g, '');
                                        if (v.length > 8) v = v.slice(0, 8);
                                        if (v.length >= 5) v = v.slice(0, 2) + '/' + v.slice(2, 4) + '/' + v.slice(4);
                                        else if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                        const parts = v.split('/');
                                        if (parts.length === 3 && parts[2]?.length === 4) {
                                            setFilterDateTo(`${parts[2]}-${parts[1]}-${parts[0]}`);
                                        } else {
                                            setFilterDateTo(v);
                                        }
                                    }}
                                />
                            </Stack>
                        </Box>

                        {/* Faixa de valor */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1 }}>
                                Faixa de valor
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <TextField
                                    size="small"
                                    label="Min"
                                    type="number"
                                    value={filterPriceRange[0] || ''}
                                    onChange={e => {
                                        const val = Number(e.target.value) || 0;
                                        setFilterPriceRange(prev => [val, prev[1]]);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    label="Max"
                                    type="number"
                                    value={filterPriceRange[1] || ''}
                                    onChange={e => {
                                        const val = Number(e.target.value) || 0;
                                        setFilterPriceRange(prev => [prev[0], val]);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                    sx={{ flex: 1 }}
                                />
                            </Stack>
                            <Slider
                                value={filterPriceRange}
                                onChange={(_, val) => setFilterPriceRange(val)}
                                min={0}
                                max={500}
                                step={10}
                                valueLabelDisplay="auto"
                                valueLabelFormat={v => `R$ ${v}`}
                                sx={{ color: '#006a61' }}
                            />
                        </Box>

                        {/* Botoes */}
                        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={applyFilters}
                                fullWidth
                                sx={{
                                    bgcolor: '#006a61',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    '&:hover': { bgcolor: '#005a52' },
                                }}
                            >
                                Aplicar
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={clearFilters}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: '#ccc',
                                    color: '#666',
                                    borderRadius: '8px',
                                }}
                            >
                                Limpar
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};
